import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { from, of } from 'rxjs';
import { catchError, concatMap, map, toArray } from 'rxjs/operators';
import { LogicielCaisseReasortService } from 'app/services/logiciel-caisse/logiciel-caisse-reasort.service';
import { ProduitItem } from 'app/services/logiciel-caisse/logiciel-caisse-produit.service';
import {
  EntrepotTableRow,
  LogicielCaisseEntrepotService,
} from 'app/services/logiciel-caisse/logiciel-caisse-entrepot.service';

interface ReasortConfigRow {
  id: number;
  reference: string;
  nom: string;
  stockDispo: number;
  min_stock: number | null;
  max_stock: number | null;
  dirty: boolean;
}

@Component({
  selector: 'app-logiciel-caisse-reasort-config',
  templateUrl: './logiciel-caisse-reasort-config.component.html',
  styleUrls: ['./logiciel-caisse-reasort-config.component.scss'],
})
export class LogicielCaisseReasortConfigComponent implements OnInit {
  entrepotId = 0;
  entrepotLabel = '';
  searchTerm = '';
  loading = false;
  saving = false;
  rows: ReasortConfigRow[] = [];
  filteredRows: ReasortConfigRow[] = [];

  bulkMin: number | null = null;
  bulkMax: number | null = null;

  currentPage = 1;
  pageSize = 100;
  readonly pageSizeOptions = [100, 1000, 10000];
  totalPages = 1;
  serverTotal = 0;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly reasortService: LogicielCaisseReasortService,
    private readonly entrepotService: LogicielCaisseEntrepotService,
    private readonly toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      this.entrepotId = Number(params.get('entrepotId'));
      if (!Number.isFinite(this.entrepotId) || this.entrepotId <= 0) {
        this.toastr.error('Entrepôt invalide');
        void this.router.navigate(['/main/parametre/reasort']);
        return;
      }
      this.resolveEntrepotLabel();
      this.currentPage = 1;
      this.loadPage();
    });
  }

  goBack(): void {
    void this.router.navigate(['/main/parametre/reasort']);
  }

  applySearch(): void {
    const kw = this.searchTerm.trim().toLowerCase();
    if (!kw) {
      this.filteredRows = [...this.rows];
      return;
    }
    this.filteredRows = this.rows.filter((r) => {
      const blob = `${r.reference} ${r.nom} ${r.min_stock ?? ''} ${r.max_stock ?? ''}`.toLowerCase();
      return blob.includes(kw);
    });
  }

  onMinChange(row: ReasortConfigRow, value: string | number | null): void {
    if (value === '' || value == null) {
      row.min_stock = null;
    } else {
      const n = Number(String(value).replace(',', '.'));
      row.min_stock = Number.isFinite(n) && n >= 0 ? n : null;
    }
    row.dirty = true;
  }

  onMaxChange(row: ReasortConfigRow, value: string | number | null): void {
    if (value === '' || value == null) {
      row.max_stock = null;
    } else {
      const n = Number(String(value).replace(',', '.'));
      row.max_stock = Number.isFinite(n) && n >= 0 ? n : null;
    }
    row.dirty = true;
  }

  applyBulkToFiltered(): void {
    const min = this.bulkMin;
    const max = this.bulkMax;
    if (
      (min == null || !Number.isFinite(min) || min < 0) &&
      (max == null || !Number.isFinite(max) || max < 0)
    ) {
      this.toastr.warning('Saisissez un min et/ou un max à appliquer.');
      return;
    }
    for (const row of this.filteredRows) {
      if (min != null && Number.isFinite(min) && min >= 0) {
        row.min_stock = min;
      }
      if (max != null && Number.isFinite(max) && max >= 0) {
        row.max_stock = max;
      }
      row.dirty = true;
    }
    this.toastr.info(
      `${this.filteredRows.length} ligne(s) mises à jour localement — cliquez Enregistrer.`
    );
  }

  saveDirty(): void {
    const dirty = this.rows.filter((r) => r.dirty);
    if (!dirty.length) {
      this.toastr.info('Aucune modification à enregistrer.');
      return;
    }
    for (const r of dirty) {
      const min = Number(r.min_stock ?? 0);
      const max = Number(r.max_stock ?? 0);
      if (!Number.isFinite(min) || min < 0 || !Number.isFinite(max) || max < 0) {
        this.toastr.error(`Seuils invalides pour ${r.nom}`);
        return;
      }
      if (max > 0 && min > max) {
        this.toastr.error(`Min > Max pour ${r.nom}`);
        return;
      }
    }

    this.saving = true;
    from(dirty)
      .pipe(
        concatMap((r) =>
          this.reasortService
            .bulkUpsert(this.entrepotId, {
              min_stock: Number(r.min_stock ?? 0),
              max_stock: Number(r.max_stock ?? 0),
              product_ids: [r.id],
            })
            .pipe(
              map(() => r),
              catchError((err: Error) => {
                this.toastr.error(`${r.nom} : ${err.message}`);
                return of(null);
              })
            )
        ),
        toArray()
      )
      .subscribe({
        next: (results) => {
          const ok = results.filter(Boolean).length;
          this.saving = false;
          if (ok > 0) {
            this.toastr.success(`${ok} article(s) enregistré(s) dans reasort_config.`);
            this.loadPage();
          }
        },
        error: (err: Error) => {
          this.saving = false;
          this.toastr.error(err.message || 'Échec enregistrement');
        },
      });
  }

  onPageChange(delta: number): void {
    const next = this.currentPage + delta;
    if (next < 1 || next > this.totalPages) {
      return;
    }
    this.currentPage = next;
    this.loadPage();
  }

  onPageSizeChange(size: number | string): void {
    const n = Number(size);
    if (!this.pageSizeOptions.includes(n) || n === this.pageSize) {
      return;
    }
    this.pageSize = n;
    this.currentPage = 1;
    this.loadPage();
  }

  goToPage(page: number | string): void {
    const n = Number(page);
    if (!Number.isFinite(n) || n < 1 || n > this.totalPages || n === this.currentPage) {
      return;
    }
    this.currentPage = n;
    this.loadPage();
  }

  get pageRangeLabel(): string {
    if (this.serverTotal <= 0) {
      return '0 article';
    }
    const from = (this.currentPage - 1) * this.pageSize + 1;
    const to = Math.min(this.currentPage * this.pageSize, this.serverTotal);
    return `${from}–${to} sur ${this.serverTotal}`;
  }

  private resolveEntrepotLabel(): void {
    this.entrepotService.getEntrepotsErpTableRows().subscribe({
      next: (items: EntrepotTableRow[]) => {
        const row = items.find((r) => Number(r.id) === this.entrepotId);
        this.entrepotLabel = row?.LIBELLÉ || `Entrepôt #${this.entrepotId}`;
      },
      error: () => {
        this.entrepotLabel = `Entrepôt #${this.entrepotId}`;
      },
    });
  }

  private loadPage(): void {
    this.loading = true;
    this.reasortService
      .getProduitsForEntrepot(this.entrepotId, {
        actif: true,
        page: this.currentPage,
        limit: this.pageSize,
      })
      .subscribe({
        next: (res) => {
          this.rows = (res.items || []).map((p: ProduitItem) => this.toRow(p));
          this.serverTotal = res.pagination?.total ?? this.rows.length;
          // Ne pas écraser le choix utilisateur si l’API renvoie un limit différent
          const returnedLimit = Number(res.pagination?.limit);
          if (
            Number.isFinite(returnedLimit) &&
            this.pageSizeOptions.includes(returnedLimit)
          ) {
            this.pageSize = returnedLimit;
          }
          this.currentPage = res.pagination?.page ?? this.currentPage;
          this.totalPages = Math.max(
            1,
            res.pagination?.totalPages ??
              Math.ceil(this.serverTotal / Math.max(1, this.pageSize))
          );
          this.applySearch();
          this.loading = false;
        },
        error: (err: Error) => {
          this.rows = [];
          this.filteredRows = [];
          this.loading = false;
          this.toastr.error(err.message || 'Impossible de charger les produits');
        },
      });
  }

  private toRow(p: ProduitItem): ReasortConfigRow {
    return {
      id: Number(p.id),
      reference: String(p.reference_interne || p.code_art || '—'),
      nom: String(p.nom || 'Article'),
      stockDispo: Number(p.stock_disponible_global ?? 0) || 0,
      min_stock: p.reasort_min_stock != null ? Number(p.reasort_min_stock) : null,
      max_stock: p.reasort_max_stock != null ? Number(p.reasort_max_stock) : null,
      dirty: false,
    };
  }
}
